import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle, Users, Shield } from "lucide-react";
import heroBackground from "@/assets/hero-background-new.jpg";
import { TrustBadges, TrustBadge } from "../ui/TrustBadges";
import { addLanguagePrefix, type SupportedLanguage } from "@/lib/i18n";

const HeroSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const currentLang = i18n.language as SupportedLanguage;

  const translatedBadges: TrustBadge[] = [
    { icon: CheckCircle, value: "7,000+", label: tCommon('trustBadges.roofsCompletedShort') },
    { icon: Users, value: "25+", label: tCommon('trustBadges.yearsExperience') },
    { icon: Shield, value: "100%", label: tCommon('trustBadges.houstonOwned') },
  ];

  const navigateTo = (path: string) => {
    const langPath = addLanguagePrefix(path, currentLang);
    navigate(langPath);
  };

  return (
    <>
      <Helmet>
        <link rel="preload" as="image" href={heroBackground} />
      </Helmet>
      <section className="relative w-full min-h-[70vh] md:min-h-[85vh] flex items-center overflow-hidden pt-20 md:pt-24">
        {/* Background Image with Overlay - Ken Burns Effect */}
        <div
          role="img"
          aria-label="Altum Contractors Houston roofing team installing a new residential roof"
          className="absolute inset-0 bg-cover bg-center bg-no-repeat animate-[zoom-out-once_8s_ease-out_forwards]"
          style={{ backgroundImage: `url(${heroBackground})` }}
        />

        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 gradient-overlay-animated" />

        {/* Floating Decorative Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-primary/10 animate-float blur-3xl"
            style={{ animationDelay: '0s' }}
          />
          <div
            className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-white/5 animate-float-slow blur-2xl"
            style={{ animationDelay: '2s' }}
          />
          <div
            className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-primary/5 animate-float blur-xl hidden md:block"
            style={{ animationDelay: '1s' }}
          />
        </div>

        {/* Content Container */}
        <div className="relative w-full container mx-auto px-4">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0 py-20">
            {/* Headline - Simplified */}
            <h1
              className="font-heading font-bold text-white leading-[1.1] mb-6 text-4xl md:text-[64px] animate-fade-in"
              style={{ textShadow: '0 4px 8px rgba(0,0,0,0.4)' }}
            >
              {t('hero.headline')}
            </h1>

            {/* Subheadline - Shortened from 64 words to 20 */}
            <p
              className="text-white/90 text-lg md:text-xl leading-relaxed mb-8 max-w-[600px] mx-auto md:mx-0 animate-fade-in"
              style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)', animationDelay: '0.15s' }}
            >
              {t('hero.subheadline')}
            </p>

            {/* Trust Badges - Using standardized component */}
            <TrustBadges
              badges={translatedBadges}
              variant="hero"
              className="mb-10"
            />

            {/* Primary CTA with prominent styling */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center md:items-start justify-center md:justify-start animate-fade-in" style={{ animationDelay: '0.45s' }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  variant="cta"
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as any).fbq) {
                      (window as any).fbq('track', 'Contact', {
                        content_name: 'Get Free Inspection Button - Hero'
                      });
                    }
                    navigateTo("/book-inspection");
                  }}
                  className="w-full sm:w-auto font-bold text-lg px-10 py-4 rounded"
                >
                  {t('cta.getFreeInspection', { ns: 'common' })}
                </Button>
              </motion.div>
              {/* Secondary CTA - Text style for less visual weight */}
              <a
                href="tel:+13462200989"
                className="text-white hover:text-white/80 font-semibold text-lg transition-colors flex items-center gap-2"
              >
                {t('cta.callUs', { ns: 'common', phone: '(346) 220-0989' })}
              </a>
            </div>

            {/* Micro-copy */}
            <p
              className="text-white/70 text-sm animate-fade-in"
              style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)', animationDelay: '0.6s' }}
            >
              {t('hero.microcopy')}
            </p>
          </div>
        </div>

        {/* Bottom wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path
              d="M0 30C360 60 720 0 1080 30C1260 45 1380 45 1440 30V60H0V30Z"
              fill="hsl(0, 0%, 100%)"
            />
          </svg>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
